/**
 * Discount eligibility for payment modes (full vs installment) and programme registration scope.
 * Prefer per-discount flags; fall back to legacy SubProduct.discountEligibility.
 * Defaults keep prior behaviour (both payment flags true, stage scope all).
 *
 * `first_registration_only` = once per student across any course (any prior app/enrollment).
 * Legacy `initial_only` is normalized to `first_registration_only`.
 */

export const MODULE_STAGE_SCOPE_VALUES = ['all', 'first_registration_only', 'initial_only'];

export const DEFAULT_DISCOUNT_ELIGIBILITY = {
  allowFullPayment: true,
  allowInstallmentPlans: true,
  moduleStageScope: 'all'
};

export const normalizeModuleStageScope = (value) => {
  const scope = String(value || '').trim().toLowerCase();
  if (scope === 'initial_only' || scope === 'first_registration_only') {
    return 'first_registration_only';
  }
  return 'all';
};

export const normalizeDiscountEligibility = (input) => {
  const source = input && typeof input === 'object' ? input : {};
  return {
    allowFullPayment: source.allowFullPayment !== false,
    allowInstallmentPlans: source.allowInstallmentPlans !== false,
    moduleStageScope: normalizeModuleStageScope(source.moduleStageScope)
  };
};

/**
 * Prefer live SubProduct config; fall back to legacy Application snapshot.
 */
export const resolveDiscountEligibilitySource = (applicationOrSubProduct, subProduct = null) => {
  const sp = subProduct
    || (applicationOrSubProduct?.discounts !== undefined ? applicationOrSubProduct : null)
    || (applicationOrSubProduct?.subProductId && typeof applicationOrSubProduct.subProductId === 'object'
      ? applicationOrSubProduct.subProductId
      : null);
  if (sp?.discountEligibility) {
    return normalizeDiscountEligibility(sp.discountEligibility);
  }
  if (applicationOrSubProduct?.discountEligibility) {
    return normalizeDiscountEligibility(applicationOrSubProduct.discountEligibility);
  }
  return { ...DEFAULT_DISCOUNT_ELIGIBILITY };
};

export const resolveEligibilityForDiscount = (discount, fallbackEligibility = null) => {
  if (discount?.discountEligibility && typeof discount.discountEligibility === 'object') {
    return normalizeDiscountEligibility(discount.discountEligibility);
  }
  if (fallbackEligibility) {
    return normalizeDiscountEligibility(fallbackEligibility);
  }
  return { ...DEFAULT_DISCOUNT_ELIGIBILITY };
};

export const resolveInitialStageNumber = (mainProduct) => {
  const stages = Array.isArray(mainProduct?.moduleBasedPricing?.stages)
    ? mainProduct.moduleBasedPricing.stages
    : [];
  const numbers = stages
    .map((s) => Number(s?.stageNumber))
    .filter((n) => Number.isFinite(n) && n >= 0);
  if (numbers.length === 0) return 1;
  return Math.min(...numbers);
};

export const collectSelectedStageNumbers = (moduleSelections) => {
  if (!Array.isArray(moduleSelections) || moduleSelections.length === 0) return [];
  return moduleSelections
    .map((m) => Number(m?.stageNumber))
    .filter((n) => Number.isFinite(n) && n >= 0);
};

export const isDiscountAllowedForModuleStageScope = (eligibility, context = {}) => {
  const resolved = normalizeDiscountEligibility(eligibility);
  if (resolved.moduleStageScope === 'all') return true;

  if (resolved.moduleStageScope === 'first_registration_only') {
    if (context.hasPriorRegistration === true || context.hasPriorProgrammeEarlyBird === true) {
      return false;
    }
    return true;
  }

  return true;
};

export const isDiscountAllowedForPaymentContext = (eligibility, context = {}) => {
  const resolved = normalizeDiscountEligibility(eligibility);
  const { isModuleBased = false, paymentType = null, modulePaymentMode = null } = context;
  const mode = String(modulePaymentMode || '').trim().toLowerCase();

  if (isModuleBased) {
    if (mode === 'installment' || mode === 'module-wise') {
      return resolved.allowInstallmentPlans;
    }
    return resolved.allowFullPayment;
  }

  if (paymentType === 'installment') {
    return resolved.allowInstallmentPlans;
  }

  return resolved.allowFullPayment;
};

export const isDiscountRecordAllowedForPaymentContext = (
  discount,
  context = {},
  fallbackEligibility = null
) => {
  const eligibility = resolveEligibilityForDiscount(discount, fallbackEligibility);
  if (!isDiscountAllowedForPaymentContext(eligibility, context)) return false;
  return isDiscountAllowedForModuleStageScope(eligibility, context);
};

export const hasAnyDiscountAllowedForPaymentContext = (
  discounts,
  context = {},
  fallbackEligibility = null
) => {
  if (!Array.isArray(discounts) || discounts.length === 0) return false;
  return discounts.some((discount) =>
    isDiscountRecordAllowedForPaymentContext(discount, context, fallbackEligibility)
  );
};

/**
 * Seed missing per-discount flags from legacy sub-product eligibility.
 */
export const seedDiscountEligibility = (discount, fallbackEligibility = null) => {
  const fallback = fallbackEligibility
    ? normalizeDiscountEligibility(fallbackEligibility)
    : DEFAULT_DISCOUNT_ELIGIBILITY;
  return {
    ...discount,
    discountEligibility: discount?.discountEligibility
      ? normalizeDiscountEligibility(discount.discountEligibility)
      : { ...fallback }
  };
};
